import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AlertifyService, ConfirmModalComponent, GenericTableConfig } from 'ccc-commons/core';
import { PermissionsService } from '../../core/services/permissions.service';
import { Permission } from '../../core/models/permission.model';
import { RoleService } from '../../core/services/role.service';
import { Role } from '../../core/models/role.model';
import { OrganizationService } from '../../core/services/organization.service';
import { Router } from '@angular/router';

@Component({
  selector: 'ccc-cca-system-settings',
  templateUrl: './system-settings.component.html',
  styleUrls: ['./system-settings.component.scss']
})
export class SystemSettingsComponent implements OnInit {

  @ViewChild('content')
  content: NgbModal;
  modal: NgbModalRef;

  @ViewChild('modalSave')
  modalSave: ConfirmModalComponent;

  @ViewChild('modalCancel')
  modalCancel: ConfirmModalComponent;

  roleTableConfig: GenericTableConfig;
  roleHeaders = [];
  roleRow = [];

  permissions: Permission[];
  roles: Role[];
  rolesErrors = [];

  private rootOrganizationId = '';

  isSending = false;
  isLoading = false;
  isCreating = false;
  isEditing = false;
  hasRoleChanges = false;

  private roleToSave: Role;
  private roleBeforeChanges = {
    name: '',
    organizationId: '',
    permissions: [],
    published: false,
    editable: false,
    isEditingRole: false,
    hasChanges: false,
    id: ''
  };


  constructor(private modalService: NgbModal,
    private permissionsService: PermissionsService,
    private organizationService: OrganizationService,
    private roleService: RoleService,
    private router: Router,
    private alert: AlertifyService) { }

  ngOnInit() {
    setTimeout(() => {
      this.configureTable();
      this.onOpen();
    }, 100);
  }

  onOpen() {
    this.setDefaultValues();
    this.handleModal();
    this.getRootOrganization();
  }

  onCancel() {
    if (this.isEditing && this.hasRoleChanges) {
      this.modalCancel.onOpen();

    } else if (this.isCreating && this.hasRoleChanges) {
      this.modalCancel.onOpen();

    } else if (this.isEditing || this.isCreating) {
      this.modalSave.onOpen();

    } else {
      this.modal.close();
    }
  }

  handleModal() {
    this.modal = this.modalService.open(this.content, {
      centered: true,
      windowClass: 'animated faster zoomIn modal-custom',
      size: 'lg',
      backdrop: 'static',
      beforeDismiss: () => {
        if (this.hasRoleChanges) {
          this.modalSave.onOpen();
          return false;
        }
        return true;
      }
    });

    this.modal.result.then(
      () => {
        this.setDefaultValues();
        this.router.navigate([{ outlets: { modal: null } }]);

      },
      () => {
        if (this.hasRoleChanges) {
          this.modalSave.onOpen();
        }
        this.router.navigate([{ outlets: { modal: null } }]);

      }
    );
  }

  private configureTable() {
    this.roleTableConfig = new GenericTableConfig(
      this.roleHeaders,
      this.roleRow
    );
  }

  getRootOrganization() {
    this.isLoading = true;

    const params = { level: '1', skip: 0, limit: 20 };
    this.organizationService.getOrganizationSuggestions(params).subscribe((resp) => {
      if (resp.body.length > 0) {
        this.rootOrganizationId = resp.body[0].id;
        this.getPermissions();
      }
    }, error => {
      this.alert.error(error);
    });
  }

  getPermissions() {
    this.permissionsService.getPermissions().subscribe((resp) => {
      this.permissions = resp.body;
      this.getRoles();
    },
      error => {
        this.alert.error(error);
      });
  }

  getRoles() {
    this.roleService.getRoles(this.rootOrganizationId).subscribe((resp) => {
      this.roles = resp.body;
      this.isLoading = false;
    }, error => {
      this.alert.error(error);
    });
  }

  saveRolesChanges() {
    this.isSending = true;

    if (this.roleToSave.hasChanges) {

      const roleToSave = {
        name: this.roleToSave.name,
        published: this.roleToSave.published,
        editable: this.roleToSave.editable,
        permissions: this.roleToSave.permissions
      };

      if (this.roleToSave.id === undefined) {
        this.createRole(roleToSave);
      } else {
        this.editRole(roleToSave);
      }
    }
  }

  createRole(role) {
    this.roleService.createRole(this.rootOrganizationId, role).subscribe((response) => {
      this.isSending = false;
      this.isCreating = false;
      this.hasRoleChanges = false;
      this.roleToSave = undefined;
      this.setRoleOnEditionMode(response.body['id']);
      this.alert.success('Organization role created successfully');

    }, err => {
      if (this.roleToSave.name === '' || this.roleToSave.permissions.length === 0) {
        this.alert.error('Error creating role you have to provide a least 1 permission and the name can not be blank');
      } else {
        this.alert.error(err);
      }
      this.isSending = false;
    });
  }

  editRole(role) {
    this.roleService.updateRole(this.roleToSave.id, role).subscribe((response) => {
      this.setRoleOnEditionMode(response.body['id']);
      this.isSending = false;
      this.hasRoleChanges = false;
      this.isEditing = false;
      this.roleToSave = undefined;

      this.alert.success('Organization role updated successfully');
    }, err => {
      if (this.roleToSave.name === '' || this.roleToSave.permissions.length === 0) {
        this.alert.error('Error updating role you have to provide a least 1 permission and the name can not be blank');
      } else {
        this.alert.error(err);
      }
      this.isSending = false;
    });
  }

  markRolePermission(permission: Permission, rolePermissions: string[]) {
    return rolePermissions.some(rolePermission => rolePermission === permission.key);
  }

  onCheckRolePermission(event, role: Role, permision: Permission) {
    let isPermissionOnRole = false;
    for (const rolePermission of role.permissions) {
      if (rolePermission === permision.key) {
        isPermissionOnRole = true;
      }
    }

    if (!isPermissionOnRole) {
      const permisionToAdd = permision;
      permisionToAdd.isChecked = event.target.checked;
      role.permissions.push(permisionToAdd.key);
    }

    role.hasChanges = true;
    this.hasRoleChanges = true;
  }

  onChangeRolePublished(event, role: Role) {
    role.published = event;
    role.hasChanges = true;
    this.hasRoleChanges = true;
  }

  onEditRole(role: Role) {

    this.roleBeforeChanges = {
      name: role.name,
      organizationId: role.organizationId,
      permissions: [],
      published: role.published,
      editable: role.editable,
      isEditingRole: false,
      hasChanges: false,
      id: role.id
    };

    for (const permission of role.permissions) {
      this.roleBeforeChanges.permissions.push(permission);
    }

    this.roleToSave = role;
    this.roleToSave.isEditingRole = true;
    this.isEditing = true;
    this.rolesErrors[role.id] = [];
    this.rolesErrors[role.id]['name'] = [];
  }

  onChangeRoleName(role, event) {
    role.name = event;
    role.hasChanges = true;
    this.hasRoleChanges = true;
  }

  onAddNewRole() {
    this.isCreating = true;
    this.hasRoleChanges = true;
    const newRole = new Role('', this.rootOrganizationId, [], false, false, true, true, undefined);
    this.roles.unshift(newRole);
    this.roleToSave = this.roles[0];
  }

  onCloseConfirmModal(close) {
    if (this.isEditing && close) {
      this.cancelRoleEdition();
    } else if (this.isCreating && close) {
      this.cancelCreation();
    } else if (close) {
      this.modal.close();
    }
  }

  setDefaultValues() {
    this.rootOrganizationId = '';
    this.isSending = false;
    this.isCreating = false;
    this.hasRoleChanges = false;
    this.isEditing = false;
    this.roleToSave = undefined;
    this.roleBeforeChanges = {
      name: '',
      organizationId: '',
      permissions: [],
      published: false,
      editable: false,
      isEditingRole: false,
      hasChanges: false,
      id: ''
    };
  }

  setRoleOnEditionMode(roleId: string) {
    for (const role of this.roles) {
      if (roleId === role.id) {
        role.isEditingRole = false;
        role.hasChanges = false;
        break;
      } else if (role.id === undefined) {
        role.id = roleId;
        role.isEditingRole = false;
        role.hasChanges = false;
        break;
      }
    }
  }

  cancelRoleEdition() {
    for (const role of this.roles) {
      if (role.isEditingRole) {
        role.id = this.roleBeforeChanges['id'];
        role.name = this.roleBeforeChanges['name'];
        role.organizationId = this.roleBeforeChanges['organizationId'];
        role.permissions = this.roleBeforeChanges['permissions'];
        role.published = this.roleBeforeChanges['published'];
        role.editable = this.roleBeforeChanges['editable'];
        role.isEditingRole = false;
        role.hasChanges = false;
        break;
      }
    }
    this.hasRoleChanges = false;
    this.isEditing = false;
  }

  cancelCreation() {
    this.roles.shift();
    this.hasRoleChanges = false;
    this.isCreating = false;
  }

}
